import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, inr, fdate } from "@/api";
import { PageHeader, EmptyState } from "@/components/Shared";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Forklift, Truck, Trash2, Pencil, BookOpen, Receipt } from "lucide-react";

const selCls = "w-full h-10 rounded-lg border border-slate-300 bg-slate-50/80 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none cursor-pointer px-3 text-sm text-slate-900";
const inputCls = "bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900";
const MODES = ["Cash", "UPI", "Bank Transfer", "Cheque"];
const EMPTY_T = { name: "", phone: "", vehicle_no: "", gstin: "", note: "" };
const EMPTY_E = { transporter_id: "", amount: "", date: "", payment_mode: "", account_id: "", note: "" };
const EMPTY_B = { transporter_id: "", bill_no: "", amount: "", date: "", note: "" };

export default function Transport() {
  const { user } = useAuth();
  const [transporters, setTransporters] = useState([]);
  const [entries, setEntries] = useState([]);
  const [bills, setBills] = useState([]);
  const [banks, setBanks] = useState([]);
  const [tOpen, setTOpen] = useState(false);
  const [eOpen, setEOpen] = useState(false);
  const [bOpen, setBOpen] = useState(false);
  const [tForm, setTForm] = useState(EMPTY_T);
  const [eForm, setEForm] = useState(EMPTY_E);
  const [bForm, setBForm] = useState(EMPTY_B);
  const [tEditId, setTEditId] = useState(null);
  const [eEditId, setEEditId] = useState(null);
  const [bEditId, setBEditId] = useState(null);
  const [ledgerFor, setLedgerFor] = useState(null); // transporter object
  const [ledger, setLedger] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/transporters").then((r) => setTransporters(r.data)).catch(() => {});
    api.get("/transport-entries").then((r) => setEntries(r.data)).catch(() => {});
    api.get("/transport-bills").then((r) => setBills(r.data)).catch(() => {});
    api.get("/banks").then((r) => setBanks(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!ledgerFor) return setLedger(null);
    api.get(`/transporters/${ledgerFor.transporter_id}/ledger`).then((r) => setLedger(r.data)).catch(() => {});
  }, [ledgerFor, bills, entries]);

  const totalSpent = entries.reduce((s, e) => s + e.amount, 0);
  const totalBilled = bills.reduce((s, b) => s + b.amount, 0);
  const errText = (err, fb) => { const d = err.response?.data?.detail; return typeof d === "string" ? d : fb; };

  const saveTransporter = async (e) => {
    e.preventDefault();
    if (!tForm.name.trim()) return toast.error("Transporter name required");
    setBusy(true);
    try {
      if (tEditId) {
        await api.put(`/transporters/${tEditId}`, tForm);
        toast.success("Transporter updated");
      } else {
        await api.post("/transporters", tForm);
        toast.success("Transporter added");
      }
      setTOpen(false); setTForm(EMPTY_T); setTEditId(null); load();
    } catch (err) { toast.error(errText(err, "Save failed")); }
    finally { setBusy(false); }
  };

  const removeTransporter = async (t) => {
    if (!window.confirm(`Delete transporter ${t.name}?`)) return;
    try {
      await api.delete(`/transporters/${t.transporter_id}`);
      toast.success("Transporter deleted");
      if (ledgerFor?.transporter_id === t.transporter_id) setLedgerFor(null);
      load();
    } catch (err) { toast.error(errText(err, "Delete failed")); }
  };

  const saveEntry = async (e) => {
    e.preventDefault();
    if (!eForm.transporter_id) return toast.error("Select a transporter");
    if (!eForm.account_id) return toast.error("Select a bank / cash account");
    if (!eForm.payment_mode) return toast.error("Select a payment mode");
    if (Number(eForm.amount) <= 0) return toast.error("Amount must be positive");
    setBusy(true);
    try {
      const payload = { ...eForm, amount: Number(eForm.amount), date: eForm.date || undefined };
      if (eEditId) {
        await api.put(`/transport-entries/${eEditId}`, payload);
        toast.success("Transport expense updated — expense tab & transporter ledger synced");
      } else {
        await api.post("/transport-entries", payload);
        toast.success("Transport expense saved — reflected in Income & Expense and transporter ledger");
      }
      setEOpen(false); setEForm(EMPTY_E); setEEditId(null); load();
    } catch (err) { toast.error(errText(err, "Save failed")); }
    finally { setBusy(false); }
  };

  const removeEntry = async (en) => {
    if (!window.confirm(`Delete this ${inr(en.amount)} expense for ${en.transporter_name}? The linked expense entry will also be removed.`)) return;
    try {
      await api.delete(`/transport-entries/${en.entry_id}`);
      toast.success("Transport expense deleted");
      load();
    } catch (err) { toast.error(errText(err, "Delete failed")); }
  };

  const saveBill = async (e) => {
    e.preventDefault();
    if (!bForm.transporter_id) return toast.error("Select a transporter");
    if (Number(bForm.amount) <= 0) return toast.error("Amount must be positive");
    setBusy(true);
    try {
      const payload = { ...bForm, amount: Number(bForm.amount), date: bForm.date || undefined };
      if (bEditId) {
        await api.put(`/transport-bills/${bEditId}`, payload);
        toast.success("Bill updated — transporter ledger adjusted");
      } else {
        await api.post("/transport-bills", payload);
        toast.success("Bill added to transporter ledger");
      }
      setBOpen(false); setBForm(EMPTY_B); setBEditId(null); load();
    } catch (err) { toast.error(errText(err, "Save failed")); }
    finally { setBusy(false); }
  };

  const removeBill = async (b) => {
    if (!window.confirm(`Delete bill ${b.bill_no || ""} (${inr(b.amount)}) from ${b.transporter_name}? A reversal entry will post to their ledger.`)) return;
    try {
      await api.delete(`/transport-bills/${b.bill_id}`);
      toast.success("Bill deleted — ledger reversed");
      load();
    } catch (err) { toast.error(errText(err, "Delete failed")); }
  };

  return (
    <div data-testid="transport-page">
      <PageHeader title="Transport" sub="Transporters, their bills and every freight payment — payments auto-reflect in the Income & Expense tab">
        {user?.role === "admin" && (
          <Button onClick={() => { setBForm(EMPTY_B); setBEditId(null); setBOpen(true); }} data-testid="add-bill-btn" variant="outline"
            className="border-slate-300 text-slate-700 hover:border-emerald-500/50 rounded-full">
            <Receipt className="w-4 h-4 mr-1" /> New Bill
          </Button>
        )}
        <Button onClick={() => { setTForm(EMPTY_T); setTEditId(null); setTOpen(true); }} data-testid="add-transporter-btn" variant="outline"
          className="border-slate-300 text-slate-700 hover:border-emerald-500/50 rounded-full">
          <Plus className="w-4 h-4 mr-1" /> Add Transporter
        </Button>
        <Button onClick={() => { setEForm(EMPTY_E); setEEditId(null); setEOpen(true); }} data-testid="add-transport-entry-btn"
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full transition-colors duration-200">
          <Plus className="w-4 h-4 mr-1" /> New Expense
        </Button>
      </PageHeader>

      {/* Transporters */}
      <div className="mb-8">
        <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Forklift className="w-4 h-4 text-emerald-600" /> Transporters ({transporters.length})
        </h3>
        {transporters.length === 0 ? (
          <EmptyState icon={Forklift} title="No transporters yet" sub="Add your logistics partners" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {transporters.map((t) => (
              <div key={t.transporter_id} className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-emerald-500/40 transition-colors duration-200" data-testid={`transporter-card-${t.transporter_id}`}>
                <div className="flex items-center gap-3">
                  <span className="w-11 h-11 rounded-xl bg-emerald-500/12 border border-emerald-500/35 flex items-center justify-center">
                    <Forklift className="w-5 h-5 text-emerald-600" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 truncate">{t.name}</p>
                    <p className="text-xs text-slate-500 truncate">{[t.vehicle_no, t.phone].filter(Boolean).join(" · ") || "—"}</p>
                  </div>
                  <button onClick={() => { setTForm({ name: t.name, phone: t.phone || "", vehicle_no: t.vehicle_no || "", gstin: t.gstin || "", note: t.note || "" }); setTEditId(t.transporter_id); setTOpen(true); }}
                    data-testid={`transporter-edit-${t.transporter_id}`}
                    className="text-emerald-600 hover:text-emerald-700 p-1.5 rounded-lg hover:bg-emerald-500/10 transition-colors duration-150">
                    <Pencil className="w-4 h-4" />
                  </button>
                  {user?.role === "admin" && (
                    <button onClick={() => removeTransporter(t)} data-testid={`transporter-delete-${t.transporter_id}`}
                      className="text-red-600 hover:text-red-700 p-1.5 rounded-lg hover:bg-red-500/10 transition-colors duration-150">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {t.gstin && <p className="text-[11px] font-mono2 text-emerald-600 mt-2">GSTIN: {t.gstin}</p>}
                <button onClick={() => setLedgerFor(t)} data-testid={`transporter-ledger-${t.transporter_id}`}
                  className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-300 hover:border-emerald-500/50 text-slate-700 hover:text-emerald-700 text-xs font-semibold py-2 transition-colors duration-150">
                  <BookOpen className="w-3.5 h-3.5" /> View Ledger
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bills */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <Receipt className="w-4 h-4 text-emerald-600" /> Transporter Bills ({bills.length})
        </h3>
        <span className="font-mono2 text-sm text-slate-600">Billed: <span className="text-amber-600 font-bold" data-testid="bills-total">{inr(totalBilled)}</span></span>
      </div>
      {bills.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto mb-8">
          <table className="w-full text-sm min-w-[700px]" data-testid="bills-table">
            <thead>
              <tr className="text-left text-xs font-mono2 uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-5 py-3">Date</th><th className="px-5 py-3">Bill No</th>
                <th className="px-5 py-3">Transporter</th><th className="px-5 py-3">Note</th>
                <th className="px-5 py-3 text-right">Amount</th>
                {user?.role === "admin" && <th className="px-5 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {bills.map((b) => (
                <tr key={b.bill_id} className="hover:bg-slate-50 transition-colors duration-150" data-testid={`bill-row-${b.bill_id}`}>
                  <td className="px-5 py-3.5 text-slate-600">{fdate(b.date)}</td>
                  <td className="px-5 py-3.5 font-mono2 font-semibold text-slate-900">{b.bill_no || "—"}</td>
                  <td className="px-5 py-3.5 text-slate-800">{b.transporter_name}</td>
                  <td className="px-5 py-3.5 text-slate-500">{b.note || "—"}</td>
                  <td className="px-5 py-3.5 text-right font-mono2 font-bold text-amber-600">{inr(b.amount)}</td>
                  {user?.role === "admin" && (
                    <td className="px-5 py-3.5 text-right whitespace-nowrap">
                      <button onClick={() => { setBForm({ transporter_id: b.transporter_id, bill_no: b.bill_no || "", amount: b.amount, date: b.date, note: b.note || "" }); setBEditId(b.bill_id); setBOpen(true); }}
                        data-testid={`bill-edit-${b.bill_id}`}
                        className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-500/10 transition-colors duration-150">
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button onClick={() => removeBill(b)} data-testid={`bill-delete-${b.bill_id}`}
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-500/10 transition-colors duration-150">
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Expenses */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <Truck className="w-4 h-4 text-emerald-600" /> Transport Payments ({entries.length})
        </h3>
        <span className="font-mono2 text-sm text-slate-600">Paid: <span className="text-red-600 font-bold" data-testid="transport-total">{inr(totalSpent)}</span></span>
      </div>
      {entries.length === 0 ? (
        <EmptyState icon={Truck} title="No transport payments yet" sub="Freight, loading, delivery charges — recorded here" />
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]" data-testid="transport-entries-table">
            <thead>
              <tr className="text-left text-xs font-mono2 uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-5 py-3">Date</th><th className="px-5 py-3">Transporter</th>
                <th className="px-5 py-3">Vehicle</th><th className="px-5 py-3">Mode</th>
                <th className="px-5 py-3">Account</th><th className="px-5 py-3">Note</th>
                <th className="px-5 py-3 text-right">Amount</th><th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {entries.map((en) => (
                <tr key={en.entry_id} className="hover:bg-slate-50 transition-colors duration-150" data-testid={`transport-entry-row-${en.entry_id}`}>
                  <td className="px-5 py-3.5 text-slate-600">{fdate(en.date)}</td>
                  <td className="px-5 py-3.5 font-medium text-slate-900">{en.transporter_name}</td>
                  <td className="px-5 py-3.5 font-mono2 text-xs text-slate-600">{en.vehicle_no || "—"}</td>
                  <td className="px-5 py-3.5">
                    <span className="px-2 py-0.5 rounded-full border border-blue-500/40 text-blue-600 bg-blue-500/10 text-[11px] font-medium">{en.payment_mode}</span>
                  </td>
                  <td className="px-5 py-3.5 text-slate-600">{en.account_name}</td>
                  <td className="px-5 py-3.5 text-slate-500">{en.note || "—"}</td>
                  <td className="px-5 py-3.5 text-right font-mono2 font-bold text-red-600">−{inr(en.amount)}</td>
                  <td className="px-5 py-3.5 text-right whitespace-nowrap">
                    <button onClick={() => { setEForm({ transporter_id: en.transporter_id, amount: en.amount, date: en.date, payment_mode: en.payment_mode, account_id: en.account_id, note: en.note || "" }); setEEditId(en.entry_id); setEOpen(true); }}
                      data-testid={`transport-entry-edit-${en.entry_id}`}
                      className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-500/10 transition-colors duration-150">
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                    {user?.role === "admin" && (
                      <button onClick={() => removeEntry(en)} data-testid={`transport-entry-delete-${en.entry_id}`}
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-500/10 transition-colors duration-150">
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Transporter dialog */}
      <Dialog open={tOpen} onOpenChange={(v) => { setTOpen(v); if (!v) { setTForm(EMPTY_T); setTEditId(null); } }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="transporter-dialog">
          <DialogHeader><DialogTitle>{tEditId ? "Edit Transporter" : "Add Transporter"}</DialogTitle></DialogHeader>
          <form onSubmit={saveTransporter} className="space-y-4 mt-2">
            {[
              ["Name *", "name", true, "e.g. VRL Logistics"],
              ["Phone", "phone", false, "+91…"],
              ["Vehicle No", "vehicle_no", false, "MH-12-AB-1234"],
              ["GSTIN", "gstin", false, "Optional"],
              ["Note", "note", false, "Optional"],
            ].map(([l, k, req, ph]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">{l}</Label>
                <Input required={req} value={tForm[k]} placeholder={ph} onChange={(e) => setTForm({ ...tForm, [k]: e.target.value })} data-testid={`transporter-${k}-input`} className={inputCls} />
              </div>
            ))}
            <Button type="submit" disabled={busy} data-testid="transporter-save-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition-colors duration-200">
              {busy ? "Saving…" : tEditId ? "Save Changes" : "Add Transporter"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Expense dialog */}
      <Dialog open={eOpen} onOpenChange={(v) => { setEOpen(v); if (!v) { setEForm(EMPTY_E); setEEditId(null); } }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="transport-entry-dialog">
          <DialogHeader><DialogTitle>{eEditId ? "Edit Transport Payment" : "New Transport Payment"}</DialogTitle></DialogHeader>
          <form onSubmit={saveEntry} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Transporter *</Label>
              <select value={eForm.transporter_id} onChange={(e) => setEForm({ ...eForm, transporter_id: e.target.value })} data-testid="transport-entry-transporter-select" className={selCls}>
                <option value="">Select transporter…</option>
                {transporters.map((t) => <option key={t.transporter_id} value={t.transporter_id}>{t.name}{t.vehicle_no ? ` · ${t.vehicle_no}` : ""}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Amount (₹) *</Label>
                <Input type="number" step="0.01" min="0" required value={eForm.amount} onChange={(e) => setEForm({ ...eForm, amount: e.target.value })} data-testid="transport-entry-amount-input" className={`${inputCls} font-mono2`} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Date</Label>
                <Input type="date" value={eForm.date} onChange={(e) => setEForm({ ...eForm, date: e.target.value })} data-testid="transport-entry-date-input" className={inputCls} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Paid From Account *</Label>
              <select value={eForm.account_id} onChange={(e) => setEForm({ ...eForm, account_id: e.target.value })} data-testid="transport-entry-account-select" className={selCls}>
                <option value="">Select account…</option>
                {banks.map((b) => <option key={b.account_id} value={b.account_id}>{b.name} — {inr(b.balance)}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Payment Mode *</Label>
              <select value={eForm.payment_mode} onChange={(e) => setEForm({ ...eForm, payment_mode: e.target.value })} data-testid="transport-entry-mode-select" className={selCls}>
                <option value="">Select mode…</option>
                {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Note</Label>
              <Input value={eForm.note} onChange={(e) => setEForm({ ...eForm, note: e.target.value })} placeholder="e.g. Freight for GE-0004 dispatch" data-testid="transport-entry-note-input" className={inputCls} />
            </div>
            <Button type="submit" disabled={busy} data-testid="transport-entry-save-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition-colors duration-200">
              {busy ? "Saving…" : eEditId ? "Save Changes" : "Save Payment"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bill dialog (admin) */}
      <Dialog open={bOpen} onOpenChange={(v) => { setBOpen(v); if (!v) { setBForm(EMPTY_B); setBEditId(null); } }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="bill-dialog">
          <DialogHeader><DialogTitle>{bEditId ? "Edit Transporter Bill" : "New Transporter Bill"}</DialogTitle></DialogHeader>
          <form onSubmit={saveBill} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Transporter *</Label>
              <select value={bForm.transporter_id} onChange={(e) => setBForm({ ...bForm, transporter_id: e.target.value })} data-testid="bill-transporter-select" className={selCls}>
                <option value="">Select transporter…</option>
                {transporters.map((t) => <option key={t.transporter_id} value={t.transporter_id}>{t.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Bill No</Label>
                <Input value={bForm.bill_no} onChange={(e) => setBForm({ ...bForm, bill_no: e.target.value })} placeholder="e.g. VRL-1023" data-testid="bill-no-input" className={`${inputCls} font-mono2`} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Date</Label>
                <Input type="date" value={bForm.date} onChange={(e) => setBForm({ ...bForm, date: e.target.value })} data-testid="bill-date-input" className={inputCls} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Amount (₹) *</Label>
              <Input type="number" step="0.01" min="0" required value={bForm.amount} onChange={(e) => setBForm({ ...bForm, amount: e.target.value })} data-testid="bill-amount-input" className={`${inputCls} font-mono2`} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Note</Label>
              <Input value={bForm.note} onChange={(e) => setBForm({ ...bForm, note: e.target.value })} placeholder="e.g. October freight" data-testid="bill-note-input" className={inputCls} />
            </div>
            <Button type="submit" disabled={busy} data-testid="bill-save-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition-colors duration-200">
              {busy ? "Saving…" : bEditId ? "Save Changes" : "Add Bill"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transporter ledger dialog */}
      <Dialog open={Boolean(ledgerFor)} onOpenChange={(v) => { if (!v) setLedgerFor(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl" data-testid="transporter-ledger-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-emerald-600" /> {ledgerFor?.name} — Ledger
            </DialogTitle>
          </DialogHeader>
          {ledger && (
            <div className="mt-2">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-slate-500">Bills (Cr — we owe) vs payments (Dr — we paid)</p>
                <p className="font-mono2 text-sm">Balance: <span className={`font-bold ${ledger.balance > 0 ? "text-amber-600" : "text-emerald-600"}`} data-testid="transporter-ledger-balance">{inr(Math.abs(ledger.balance))}{ledger.balance > 0 ? " payable" : " settled"}</span></p>
              </div>
              {ledger.entries.length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center">No bills or payments yet</p>
              ) : (
                <div className="max-h-[55vh] overflow-y-auto rounded-xl border border-slate-200">
                  <table className="w-full text-sm" data-testid="transporter-ledger-table">
                    <thead>
                      <tr className="text-left text-xs font-mono2 uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                        <th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">Particulars</th>
                        <th className="px-4 py-2.5 text-right">Paid (Dr)</th><th className="px-4 py-2.5 text-right">Billed (Cr)</th>
                        <th className="px-4 py-2.5 text-right">Balance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {ledger.entries.map((e) => (
                        <tr key={e.entry_id} className="hover:bg-slate-50" data-testid={`tledger-row-${e.entry_id}`}>
                          <td className="px-4 py-2.5 text-slate-600">{fdate(e.date)}</td>
                          <td className="px-4 py-2.5 text-slate-800">{e.description}</td>
                          <td className="px-4 py-2.5 text-right font-mono2 text-red-600">{e.debit ? inr(e.debit) : "—"}</td>
                          <td className="px-4 py-2.5 text-right font-mono2 text-amber-600">{e.credit ? inr(e.credit) : "—"}</td>
                          <td className="px-4 py-2.5 text-right font-mono2 font-semibold text-slate-900">{inr(Math.abs(e.balance))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
